import { useSyncExternalStore } from "react";

// A tiny module-scoped pubsub for "is the user's session locked right
// now".  The pattern: the queryClient's 401 interceptor and fetchMe
// dispatch `cerebellum:locked`; on success they clear it.  Guards
// (AuthGuard, route code) subscribe via `useLocked` and stay in place
// while the lock overlay is up instead of redirecting to /login.

let locked = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

export function setLocked(next: boolean): void {
  if (locked === next) return;
  locked = next;
  notify();
}

export function getLocked(): boolean {
  return locked;
}

// React hook — re-renders subscribers when lock state flips. Uses
// useSyncExternalStore so SSR is safe and concurrent mode doesn't
// tear.
export function useLocked(): boolean {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => locked,
    () => false,
  );
}

// Wire the DOM events into the store. Safe to call once at app
// startup — re-entries are no-ops because we guard on listener
// registration.
let wired = false;
export function wireLockEvents(): void {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("cerebellum:locked", () => setLocked(true));
  // Explicit unlock signals clear the store. fetchMe fires
  // cerebellum:unlocked when /api/me returns 200, and the overlay's
  // onUnlock handler fires it too.
  window.addEventListener("cerebellum:unlocked", () => setLocked(false));
  // Auth fully gone: clear any stale lock flag too. Without this the
  // overlay-less app body would render (AuthGuard previously suppressed
  // its /login redirect while locked) and the user would sit on a
  // broken page.
  window.addEventListener("cerebellum:auth-expired", () => setLocked(false));
}
